/** Desktop slide-out max-height presets (px). Mobile always uses full viewport. */
export const SLIDE_OUT_PANEL_MAX_HEIGHT_PRESETS = [600, 700, 800, 900, 1000]

/** Default desktop cap when admin never saved `maxHeight` (legacy bots). */
export const SLIDE_OUT_PANEL_DEFAULT_MAX_HEIGHT_PX = 800

/**
 * Desktop slide-out panel width from admin (px). Builder allows 300–600.
 *
 * @param {unknown} raw
 * @returns {number}
 */
export function clampSlideOutPanelWidthPx(raw) {
  const n = Number(raw)
  if (!Number.isFinite(n) || n <= 0) return 400
  return Math.min(600, Math.max(300, Math.round(n)))
}

/**
 * Desktop max height cap for slide-out (`min(100dvh, Npx)`). Undefined = full viewport height.
 *
 * @param {unknown} raw
 * @returns {number|undefined}
 */
export function normalizeSlideOutPanelMaxHeightPx(raw) {
  if (raw == null || raw === '') return undefined
  const s = String(raw).toLowerCase().trim()
  if (s === 'full' || s === 'viewport' || s === '100%') return undefined
  const n = Math.round(Number(raw))
  if (!Number.isFinite(n)) return undefined
  return SLIDE_OUT_PANEL_MAX_HEIGHT_PRESETS.includes(n) ? n : undefined
}

/**
 * Resolve stored slide-out max height. When the builder never persisted
 * `maxHeight`, default to {@link SLIDE_OUT_PANEL_DEFAULT_MAX_HEIGHT_PX}.
 * Explicit `full` still yields `undefined` (uncapped viewport height).
 *
 * @param {unknown} raw
 * @param {{ keyPresent?: boolean }} [opts]
 * @returns {number|undefined}
 */
export function resolveSlideOutPanelMaxHeightPx(raw, { keyPresent = true } = {}) {
  if (!keyPresent) {
    return SLIDE_OUT_PANEL_DEFAULT_MAX_HEIGHT_PX
  }
  return normalizeSlideOutPanelMaxHeightPx(raw)
}

/** Layout keys that define desktop slide-out panel size/position (shared across surfaces). */
export const SLIDE_OUT_PANEL_LAYOUT_KEYS = [
  'slideOutPanelEdge',
  'slideOutPanelWidth',
  'slideOutPanelMaxHeight',
  'slideOutMinWidth',
  'slideOutMaxWidth',
]

/**
 * @param {Record<string, unknown>|null|undefined} layout
 * @returns {Record<string, unknown>}
 */
export function pickSlideOutPanelLayout(layout) {
  if (!layout || typeof layout !== 'object') return {}
  /** @type {Record<string, unknown>} */
  const out = {}
  for (const key of SLIDE_OUT_PANEL_LAYOUT_KEYS) {
    if (layout[key] != null && layout[key] !== '') {
      out[key] = layout[key]
    }
  }
  return out
}

/**
 * Merge slide-out panel settings for an in-page (injected) surface. The
 * per-instance layout owns its slide-out dimensions (width, max height,
 * edge); the canonical (root / floating) layout only backfills keys the
 * instance left unset, so each surface can be sized independently while
 * legacy bots without injected settings still inherit sensible values.
 *
 * @param {Record<string, unknown>|null|undefined} canonicalLayout
 * @param {Record<string, unknown>|null|undefined} instanceLayout
 * @returns {Record<string, unknown>}
 */
export function mergeSlideOutPanelLayout(canonicalLayout, instanceLayout) {
  const inst =
    instanceLayout && typeof instanceLayout === 'object' ? { ...instanceLayout } : {}
  const canonical = pickSlideOutPanelLayout(canonicalLayout)
  const instancePanel = pickSlideOutPanelLayout(instanceLayout)
  // Canonical fills gaps first, then the instance's own slide-out keys win.
  return { ...inst, ...canonical, ...instancePanel }
}

/**
 * Resolve desktop slide-out max height from API layout and/or slideOutConfig.
 *
 * @param {Record<string, unknown>|null|undefined} layout
 * @param {Record<string, unknown>|null|undefined} slideOutConfig
 * @returns {number|undefined}
 */
export function resolveSlideOutPanelMaxHeightFromSources(layout, slideOutConfig) {
  if (
    layout &&
    typeof layout === 'object' &&
    Object.prototype.hasOwnProperty.call(layout, 'slideOutPanelMaxHeight')
  ) {
    return normalizeSlideOutPanelMaxHeightPx(layout.slideOutPanelMaxHeight)
  }

  const so =
    slideOutConfig && typeof slideOutConfig === 'object' && !Array.isArray(slideOutConfig)
      ? slideOutConfig
      : null
  if (so) {
    return resolveSlideOutPanelMaxHeightPx(so.maxHeight, {
      keyPresent: Object.prototype.hasOwnProperty.call(so, 'maxHeight'),
    })
  }

  return SLIDE_OUT_PANEL_DEFAULT_MAX_HEIGHT_PX
}

/**
 * Lift builder `slideOutConfig` onto flat conf slide-out panel fields.
 *
 * @param {Record<string, unknown>} conf
 * @param {{ authoritative?: boolean }} [opts] When true, slideOutConfig overrides existing values (legacy payloads).
 * @returns {Record<string, unknown>}
 */
export function applySlideOutConfigToConf(conf, { authoritative = false } = {}) {
  if (!conf || typeof conf !== 'object') return conf
  const so = conf.slideOutConfig
  if (!so || typeof so !== 'object' || Array.isArray(so)) return conf

  const pos = String(so.position ?? so.panelPosition ?? '')
    .trim()
    .toLowerCase()
  if (
    (authoritative || conf.slideOutPanelEdge == null) &&
    (pos === 'left' || pos === 'right')
  ) {
    conf.slideOutPanelEdge = pos
  }
  if ((authoritative || conf.slideOutPanelWidth == null) && so.width != null) {
    conf.slideOutPanelWidth = clampSlideOutPanelWidthPx(so.width)
  }
  if (authoritative || conf.slideOutPanelMaxHeight == null) {
    const maxHPx = resolveSlideOutPanelMaxHeightPx(so.maxHeight, {
      keyPresent: Object.prototype.hasOwnProperty.call(so, 'maxHeight'),
    })
    if (maxHPx !== undefined) {
      conf.slideOutPanelMaxHeight = maxHPx
    } else if (Object.prototype.hasOwnProperty.call(so, 'maxHeight')) {
      delete conf.slideOutPanelMaxHeight
    }
  }
  if (
    (authoritative || conf.slideOutMinWidth == null) &&
    so.minWidth != null &&
    Number.isFinite(Number(so.minWidth))
  ) {
    conf.slideOutMinWidth = Math.round(Number(so.minWidth))
  }
  if (
    (authoritative || conf.slideOutMaxWidth == null) &&
    so.maxWidth != null &&
    Number.isFinite(Number(so.maxWidth))
  ) {
    conf.slideOutMaxWidth = Math.round(Number(so.maxWidth))
  }
  return conf
}
